/**
 * StrategicAnalysisForm - Structured form for product/competitive/market analysis.
 *
 * Features:
 * - Card-based analysis type selector with icons
 * - Example prompts per analysis type (clickable chips)
 * - Optional competitor comparison
 * - Depth selector (quick/standard/comprehensive)
 */

import { useState, useCallback } from 'react';
import { Zap, Target, Users, Globe, Scale, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StrategicIntensity } from '@/hooks/useStrategicStream';

export type StrategicAnalysisType =
  | 'product_market_fit'
  | 'competitive_positioning'
  | 'go_to_market'
  | 'swot'
  | 'general_strategic';

const ANALYSIS_TYPES: {
  value: StrategicAnalysisType;
  label: string;
  description: string;
  icon: typeof Target;
}[] = [
  { value: 'product_market_fit', label: 'Product-Market Fit', description: 'Validate demand signals and market alignment', icon: Target },
  { value: 'competitive_positioning', label: 'Competitive Positioning', description: 'Map landscape and differentiation', icon: Users },
  { value: 'go_to_market', label: 'Go-to-Market', description: 'Evaluate GTM strategy and channels', icon: Globe },
  { value: 'swot', label: 'SWOT Analysis', description: 'Strengths, weaknesses, opportunities, threats', icon: Scale },
  { value: 'general_strategic', label: 'General Strategic', description: 'Comprehensive strategic assessment', icon: Brain },
];

const EXAMPLE_PROMPTS: Record<StrategicAnalysisType, string[]> = {
  product_market_fit: [
    'Notion for enterprise teams',
    'AI-powered CRM startup targeting SMBs',
    'Electric vehicle subscription service',
  ],
  competitive_positioning: [
    'Shopify vs Squarespace in e-commerce',
    'Figma in the design tools market',
    'OpenAI vs Anthropic in AI APIs',
  ],
  go_to_market: [
    'B2B SaaS expanding into Europe',
    'DTC skincare brand launching in APAC',
    'Dev tools entering enterprise segment',
  ],
  swot: [
    'Tesla in 2026',
    'Netflix streaming business',
    'Stripe payments platform',
  ],
  general_strategic: [
    'Should a mid-size SaaS go public?',
    'Build vs buy for AI capabilities',
    'Vertical SaaS consolidation plays',
  ],
};

const DEPTH_OPTIONS: { value: StrategicIntensity; label: string; description: string; estimatedCredits: string }[] = [
  { value: 'fast', label: 'Fast', description: 'Fast overview', estimatedCredits: '~30' },
  { value: 'deep', label: 'Deep', description: 'Full analysis with debate', estimatedCredits: '~55' },
];

interface StrategicAnalysisFormProps {
  onSubmit: (params: {
    subject: string;
    analysisType: StrategicAnalysisType;
    depth: StrategicIntensity;
  }) => void;
  isSubmitting?: boolean;
  disabled?: boolean;
  /** Initial subject value (from URL params) */
  initialSubject?: string;
}

export function StrategicAnalysisForm({ onSubmit, isSubmitting = false, disabled = false, initialSubject = '' }: StrategicAnalysisFormProps) {
  const [subject, setSubject] = useState(initialSubject);
  const [analysisType, setAnalysisType] = useState<StrategicAnalysisType>('general_strategic');
  const [depth, setDepth] = useState<StrategicIntensity>('fast');

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;

    onSubmit({
      subject: subject.trim(),
      analysisType,
      depth,
    });
  }, [subject, analysisType, depth, onSubmit]);

  const isValid = subject.trim().length > 0;
  const isBusy = disabled || isSubmitting;

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-xl mx-auto space-y-6">
      {/* Subject Input */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1.5">
          Subject <span className="text-red-400 ml-1">*</span>
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Company, product, or business concept..."
          disabled={isBusy}
          className={cn(
            'w-full px-4 py-3 rounded-lg',
            'bg-surface-light border border-border',
            'text-text-primary placeholder:text-text-muted',
            'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
            'transition-colors duration-200',
            isBusy && 'opacity-50 cursor-not-allowed'
          )}
        />

        {/* Example prompts — show when subject is empty */}
        {!subject.trim() && (
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-text-muted">Try:</span>
            {EXAMPLE_PROMPTS[analysisType].map((example, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSubject(example)}
                disabled={isBusy}
                className={cn(
                  'px-2.5 py-1 text-xs rounded-md',
                  'bg-surface-light/50 border border-border/50',
                  'text-text-secondary hover:text-text-primary hover:border-border-light',
                  'transition-colors',
                  isBusy && 'opacity-50 cursor-not-allowed'
                )}
              >
                {example}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Analysis Type — Card-based selector */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1.5">
          Analysis Type
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ANALYSIS_TYPES.map(type => {
            const Icon = type.icon;
            const isSelected = analysisType === type.value;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => setAnalysisType(type.value)}
                disabled={isBusy}
                className={cn(
                  'px-3 py-2.5 rounded-lg border text-left transition-all duration-200',
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-surface-light text-text-secondary hover:border-text-muted',
                  isBusy && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon className={cn('w-4 h-4', isSelected ? 'text-primary' : 'text-text-muted')} />
                  <span className="font-medium text-sm">{type.label}</span>
                </div>
                <div className="text-xs text-text-muted mt-0.5 ml-6">{type.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Depth Selector */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1.5">
          Research Depth
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {DEPTH_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => setDepth(option.value)}
              disabled={isBusy}
              className={cn(
                'px-4 py-3 rounded-lg border text-center transition-all duration-200',
                depth === option.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-surface-light text-text-secondary hover:border-text-muted',
                isBusy && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="font-medium text-sm">{option.label}</div>
              <div className="text-xs text-text-muted mt-0.5">{option.description}</div>
              <div className="text-[10px] text-text-muted mt-1 font-mono">{option.estimatedCredits} credits</div>
            </button>
          ))}
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!isValid || isSubmitting || disabled}
        className={cn(
          'w-full py-4 rounded-lg font-semibold text-base',
          'flex items-center justify-center gap-2',
          'transition-all duration-300',
          isValid && !isSubmitting && !disabled
            ? 'bg-primary text-text-primary hover:bg-primary/90 shadow-lg shadow-primary/25 hover:shadow-primary/40'
            : 'bg-surface-light text-text-muted cursor-not-allowed'
        )}
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Running Analysis...</span>
          </>
        ) : (
          <>
            <Zap className="w-5 h-5" />
            <span>Run Strategic Analysis</span>
          </>
        )}
      </button>
    </form>
  );
}
